import FadeUp from "./FadeUp";
import { Check, ArrowRight, Package, Calendar, PackageOpen } from "lucide-react";

const tiers = [
    {
        id: "starter",
        name: "Starter",
        price: "$2,000",
        cadence: "/ month",
        tag: null,
        features: [
            "75 creator outreaches per week (300 / month)",
            "Affiliate recruitment",
            "5 paid YouTube partnerships / month",
            "UGC rights & whitelisting negotiation",
            "Dedicated account manager",
            "Performance reporting",
            "Bi-weekly performance calls",
        ],
        recommended: false,
    },
    {
        id: "growth",
        name: "Growth",
        price: "$3,500",
        cadence: "/ month",
        tag: "Most Popular",
        features: [
            "150 creator outreaches per week",
            "Affiliate recruitment",
            "10 paid YouTube partnerships / month",
            "UGC rights & whitelisting negotiation",
            "Dedicated account manager",
            "Performance reporting",
            "Bi-weekly performance calls",
        ],
        recommended: true,
    },
    {
        id: "scale",
        name: "Scale",
        price: "$5,000",
        cadence: "/ month",
        tag: "Best Value",
        features: [
            "300 creator outreaches per week",
            "Affiliate recruitment",
            "20 paid YouTube partnerships / month",
            "UGC rights & whitelisting negotiation",
            "Dedicated account manager",
            "Performance reporting",
            "Priority support",
            "Bi-weekly performance calls",
        ],
        recommended: false,
    },
];

const bonuses = [
    {
        icon: Package,
        title: "Full Creator Tech Stack",
        body: "Access to creator discovery, CRM, affiliate tracking and social listening tools worth roughly $3,000 over three months.",
    },
    {
        icon: Calendar,
        title: "Bi-weekly Performance Calls",
        body: "Review performance and next-step strategy every other week, directly with me. Not an account manager.",
    },
    {
        icon: PackageOpen,
        title: "Complete Handoff",
        body: "Export creator relationships, SOPs, affiliate database, and train the internal hire so nothing walks out with me.",
    },
];

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Pricing = () => (
    <section
        id="pricing"
        data-testid="pricing-section"
        className="py-24 md:py-32 bg-white"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Pricing
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="pricing-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-[#0A0A0A] text-balance"
                    >
                        Simple pricing. Three-month engagement.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-relaxed max-w-2xl">
                        Pick the outreach volume that fits your stage. Every tier ends with the same outcome: you own the engine.
                    </p>
                </FadeUp>
            </div>

            <div className="mt-14 grid lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
                {tiers.map((t, i) => (
                    <FadeUp key={t.id} delay={0.05 + i * 0.06}>
                        <div
                            data-testid={`pricing-card-${t.id}`}
                            className={`h-full rounded-3xl p-7 md:p-8 flex flex-col ${
                                t.recommended
                                    ? "bg-[#0A0A0A] text-white border border-[#0A0A0A]"
                                    : "bg-white border border-black/[0.08]"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div
                                    className={`text-[11px] font-bold tracking-[0.2em] uppercase ${
                                        t.recommended ? "text-white/60" : "text-gray-500"
                                    }`}
                                >
                                    {t.name}
                                </div>
                                {t.tag && (
                                    <span
                                        className={`text-[10.5px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full ${
                                            t.recommended
                                                ? "bg-[#C6F432] text-[#0A0A0A]"
                                                : "bg-[#FAFAFA] text-[#0A0A0A] border border-black/[0.08]"
                                        }`}
                                    >
                                        {t.tag}
                                    </span>
                                )}
                            </div>
                            <div className="mt-6 flex items-baseline gap-1.5">
                                <span
                                    className={`font-display font-extrabold text-5xl md:text-[52px] tracking-[-0.03em] leading-none ${
                                        t.recommended ? "text-white" : "text-[#0A0A0A]"
                                    }`}
                                >
                                    {t.price}
                                </span>
                                <span
                                    className={`text-sm font-medium ${
                                        t.recommended ? "text-white/60" : "text-gray-500"
                                    }`}
                                >
                                    {t.cadence}
                                </span>
                            </div>

                            <ul className="mt-8 space-y-3 flex-1">
                                {t.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3">
                                        <Check
                                            className={`h-4 w-4 mt-1 flex-none ${
                                                t.recommended ? "text-[#C6F432]" : "text-[#0A0A0A]"
                                            }`}
                                            strokeWidth={2.5}
                                        />
                                        <span
                                            className={`text-[14.5px] leading-[1.65] ${
                                                t.recommended ? "text-white/85" : "text-gray-700"
                                            }`}
                                        >
                                            {f}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                data-testid={`pricing-cta-${t.id}`}
                                onClick={() => scrollTo("final-cta")}
                                className={`mt-10 w-full inline-flex items-center justify-center gap-2 rounded-full h-12 text-sm font-semibold transition-all hover:-translate-y-[1px] ${
                                    t.recommended
                                        ? "bg-white text-[#0A0A0A] hover:bg-[#C6F432]"
                                        : "bg-[#0A0A0A] hover:bg-black text-white"
                                }`}
                            >
                                Book a Strategy Call
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </FadeUp>
                ))}
            </div>

            {/* Bonuses */}
            <FadeUp delay={0.1}>
                <div className="mt-20 md:mt-24 max-w-3xl">
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Every tier includes
                    </p>
                    <h3 className="mt-3 font-display font-bold text-2xl md:text-[28px] tracking-[-0.02em] leading-[1.3] text-[#0A0A0A]">
                        Three bonuses baked into every plan.
                    </h3>
                </div>
            </FadeUp>
            <div className="mt-8 grid md:grid-cols-3 gap-4 md:gap-5">
                {bonuses.map((b, i) => (
                    <FadeUp key={i} delay={0.05 + i * 0.05}>
                        <div
                            data-testid={`bonus-card-${i}`}
                            className="h-full rounded-2xl border border-black/[0.08] bg-[#FAFAFA] p-6 md:p-7"
                        >
                            <div className="h-10 w-10 rounded-xl bg-white border border-black/[0.08] flex items-center justify-center">
                                <b.icon className="h-5 w-5 text-[#0A0A0A]" strokeWidth={1.8} />
                            </div>
                            <h4 className="mt-5 font-display font-bold text-[17px] tracking-tight text-[#0A0A0A] leading-[1.3]">
                                {b.title}
                            </h4>
                            <p className="mt-3 text-[14px] text-gray-600 leading-relaxed">
                                {b.body}
                            </p>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

export default Pricing;
